import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { CustomDelete, FullTable, SelectActions, SimpleModal } from 'components';
import { FindSuccessItem, FLAGGED_VALUES, Images, PermissionList, RolePermission, SaveParams, useModal, useSecondaryModal } from 'utils';
import { adminActions, appointmentActions, claimActions, httpRequestsOnSuccessActions, systemActions } from 'store';
import { ScheduleDetails, scheduleModalsStyle } from '../../../fragments/schedule';
import {
    authEnums,
    BILLABLE_ACTION_TYPE,
    billedClaimNotYet,
    checkGenerateBill,
    claimTabEnums,
    generateClaimBody,
    generateClaimHead,
} from './constants';
import { appointmentService } from '../../../store/appointment/appointment.service';
import { ServiceAppt } from '../../../fragments/schedule/core/modals/serviceAppt';
import { BillingTotalBox } from './core';

const EDIT_ACTION = 'EDIT_APPOINTMENT';
const GENERATE_ACTION = 'GENERATE_CLAIM';
const LOCK_UNLOCK = 'CHANGE_APPOINTMENT_LOCK_UNLOCK';
const RENDER_APPOINTMENTS_LIST = 'RENDER_APPOINTMENTS_LIST';
const GET_BILLABLE_CLAIMS_TOTAL = 'GET_BILLABLE_CLAIMS_TOTAL';

export const BillableAppt = ({}) => {
    const { appointments } = useSelector((state) => ({
        appointments: state.appointment.appointments,
    }));
    const billableClaimsTotal = useSelector((state) => state.claim.billableClaimsTotal);
    const issueBypasses = [
        RolePermission([PermissionList.APPT_BILL_WITH_INCOMPLETE_UNITS?.code]) && PermissionList.APPT_BILL_WITH_INCOMPLETE_UNITS?.code,
        RolePermission([PermissionList.APPT_BILL_WITH_EXCEEDED_UNITS?.code]) && PermissionList.APPT_BILL_WITH_EXCEEDED_UNITS?.code,
        RolePermission([PermissionList.APPT_BILL_WITH_EXPIRED_AUTH?.code]) && PermissionList.APPT_BILL_WITH_EXPIRED_AUTH?.code,
        RolePermission([PermissionList.APPT_BILL_WITH_UNIT_OUT_OF_RANGE?.code]) && PermissionList.APPT_BILL_WITH_UNIT_OUT_OF_RANGE?.code,
        RolePermission([PermissionList.APPT_BILL_WITH_FREQUENCY_EXCEEDED?.code]) && PermissionList.APPT_BILL_WITH_FREQUENCY_EXCEEDED?.code,
    ].filter(Boolean);
    const filteredList = appointments?.appts?.filter((i) => !checkGenerateBill(i?.issues, issueBypasses));
    const dispatch = useDispatch();
    const modalClasses = scheduleModalsStyle();
    const success = FindSuccessItem(EDIT_ACTION);
    const generateActon = FindSuccessItem(GENERATE_ACTION);
    const renderApptList = FindSuccessItem(RENDER_APPOINTMENTS_LIST);
    const [selectedList, setSelectedList] = useState([]);
    const [item, setItem] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const pushInfo = location?.state;
    const [loadId, setLoadId] = useState(null);
    const { open } = useModal();
    const { openSecondary } = useSecondaryModal();
    const [selectAllList, setSelectAllList] = useState(false);

    useEffect(() => {
        if (success) {
            getAppt('noLoad');
            dispatch(httpRequestsOnSuccessActions.removeSuccess(EDIT_ACTION));
            setItem(null);
        }
        if (renderApptList) {
            getAppt('noLoad');
            dispatch(httpRequestsOnSuccessActions.removeSuccess(RENDER_APPOINTMENTS_LIST));
        }
        if (generateActon) {
            getAppt();
            setSelectedList([]);
            dispatch(httpRequestsOnSuccessActions.removeSuccess(GENERATE_ACTION));
            SaveParams(navigate, { ...pushInfo, tabType: claimTabEnums?.PENDING });
        }
    }, [success, generateActon, renderApptList]);

    useEffect(() => {
        getAppt();
    }, [pushInfo]);

    const renderParams = () => {
        const filterInfo = {
            skip: pushInfo?.skip ? pushInfo?.skip : 0,
            limit: 50,
            status: claimTabEnums.COMPLETED,
            type: 'SERVICE',
            billStatus: 'NOT_BILLED',
            ...pushInfo,
        };

        delete filterInfo.tabType;
        delete filterInfo.page;
        delete filterInfo.clientFull;
        delete filterInfo.funderName;
        pushInfo?.orderType ? (filterInfo.orderType = pushInfo?.orderType) : delete filterInfo.orderType;
        pushInfo?.sortType ? (filterInfo.sortType = pushInfo?.sortType) : delete filterInfo.sortType;
        pushInfo?.errors?.length ? (filterInfo.errors = pushInfo.errors?.map((i) => i?.id || i)) : delete filterInfo.errors;

        if (pushInfo?.payer) {
            filterInfo.fundingSource = pushInfo?.payer;
            delete filterInfo.payer;
        } else {
            delete filterInfo.fundingSource;
        }
        // pushInfo?.payer ? filterInfo.fundingSource = pushInfo?.payer : delete filterInfo.fundingSource;

        if (pushInfo?.from) {
            filterInfo.startDate = moment.utc(pushInfo?.from).format('YYYY-MM-DD');
            delete filterInfo.from;
        } else {
            delete filterInfo.from;
        }
        if (pushInfo?.to) {
            filterInfo.endDate = moment.utc(pushInfo?.to).format('YYYY-MM-DD');
            delete filterInfo.to;
        } else {
            delete filterInfo.to;
        }

        if (pushInfo?.flag) {
            if (pushInfo?.flag === FLAGGED_VALUES.REMOVED_FROM_CLAIM) {
                filterInfo.removedFromClaimFlag = true;
            } else {
                filterInfo.incompleteUnitFlag = true;
            }
        } else {
            delete filterInfo.flag;
            delete filterInfo.removedFromClaimFlag;
            delete filterInfo.incompleteUnitFlag;
        }

        if (pushInfo?.withoutAuthId) {
            filterInfo.withoutAuthId = !(pushInfo?.withoutAuthId === authEnums.WITH_AUTH);
        } else {
            filterInfo.withoutAuthId = null;
        }

        return filterInfo;
    };

    const getAppt = (load) => {
        const filterInfo = renderParams();

        const totalParams = {
            ...filterInfo,
        };
        delete totalParams.skip;
        delete totalParams.limit;
        dispatch(appointmentActions.getAppointment(filterInfo, load));
        dispatch(claimActions.getBillableClaimsTotal(totalParams, load));
    };

    const handleCheck = (ev, item, name) => {
        ev.stopPropagation();
        ev.preventDefault();
        setSelectAllList(false);

        if (name === 'all') {
            let allList = filteredList?.length === selectedList?.length ? [] : filteredList?.map((i) => i?.id);
            setSelectedList(allList);
        } else {
            const list = [...selectedList];
            const uniqSet = new Set(list);

            if (!uniqSet.has(item?.id)) {
                list.push(item?.id);
            } else {
                list.indexOf(item?.id) !== -1 && list.splice(list.indexOf(item?.id), 1);
            }
            setSelectedList(list);
        }
        // }
    };
    const handleEdit = (item) => {
        dispatch(adminActions.getAllPayCodes(item.staff?.id));
        dispatch(systemActions.getPlaces());
        setLoadId(item?.id);

        appointmentService
            .getAppointmentByIdService(item?.id)
            .then((res) => {
                setItem(res?.data);
            })
            .finally(() => {
                setLoadId(null);
            });
    };

    const removeSelectedList = () => {
        setSelectedList([]);
        setSelectAllList(false);
    };

    const lockUnlock = (name) => {
        const lockUnlockType = name === 'lock';
        const subText =
            name === 'lock'
                ? 'Are you sure you want to lock the selected appointments? Once locked, they cannot be edited until unlocked.'
                : 'Are you sure you want to unlock the selected appointments? Once unlocked, they can be edited again.';

        const list = {
            apptIds: selectAllList ? [] : selectedList,
            all: selectAllList,

            // apptIds: selectedList,
            // all: selectedList,
        };

        open(
            <CustomDelete
                info={`Confirm ${name === 'lock' ? 'Lock' : 'Unlock'} All Appointments`}
                text={subText}
                innerText={name === 'lock' ? 'Lock Selected' : 'Unlock Selected'}
                handleDel={() => dispatch(appointmentActions.changeApptLockUnlock(list, lockUnlockType, renderParams()))}
                actionType={LOCK_UNLOCK}
                color={name === 'lock' ? null : 'blue'}
                afterSuccess={() => {
                    getAppt('noLoad');
                    setSelectedList([]);
                }}
            />
        );
    };

    const handleOpenCloseModal = (id) => {
        openSecondary(<ScheduleDetails currentId={id} getAfterSuccess={() => getAppt('noLoad')} />);
    };

    return (
        <>
            <div>
                {RolePermission([PermissionList.CLAIMS_CREATE?.code]) && !!selectedList?.length && (
                    <SelectActions
                        submitText={'Prepare Claims'}
                        submitBtnIcon={Images.addCircle}
                        selectInnetText={selectAllList ? 'Clear Selection' : ` Select all ${appointments?.count} across all pages`}
                        // selectInnetText={selectedList?.length === filteredList?.length ? 'Clear Selection' : 'Select All'}
                        // selectAll={handleCheck}
                        removeSelected={removeSelectedList}
                        actionType={GENERATE_ACTION}
                        selectedList={selectAllList ? appointments?.count : selectedList?.length}
                        // selectedList={selectedList?.length}
                        selectAll={() => {
                            if (selectAllList) {
                                removeSelectedList();
                            } else {
                                setSelectAllList(true);
                                setSelectedList(filteredList?.map((i) => i?.id));
                            }
                        }}
                        submitClaims={() =>
                            dispatch(
                                claimActions.generateClaim(
                                    {
                                        appts: selectAllList ? [] : selectedList,
                                        all: selectAllList,
                                    },
                                    renderParams()
                                )
                            )
                        }
                        afterSuccess={removeSelectedList}
                        renderOtherBtns={
                            RolePermission([PermissionList.APPT_LOCK?.code]) && (
                                <div className="flex-align-center" style={{ height: 30, gap: 8 }}>
                                    <button onClick={() => lockUnlock('lock')} className="selector-action-btn">
                                        <img src={Images.lock} alt="lock" />
                                    </button>
                                    <button onClick={() => lockUnlock('unLock')} className="selector-action-btn">
                                        <img src={Images.unlock} alt="lock" />
                                    </button>
                                </div>
                            )
                        }
                    />
                )}

                <div style={{ marginTop: 12 }}>
                    <BillingTotalBox title={'Total Billable Amount:'} total={billableClaimsTotal} actionType={GET_BILLABLE_CLAIMS_TOTAL} />
                    <FullTable
                        margin={'0'}
                        head={generateClaimHead(handleCheck, filteredList, selectedList, selectAllList)}
                        body={generateClaimBody(handleCheck, appointments?.appts, selectedList, handleEdit, loadId, selectAllList, issueBypasses)}
                        loadingType={BILLABLE_ACTION_TYPE}
                        list={appointments?.appts}
                        listCount={appointments?.count}
                        notYet={billedClaimNotYet}
                        handleClick={handleOpenCloseModal}
                        selectedList={selectedList}
                        selectAllList={selectAllList}
                    />
                </div>
            </div>

            <SimpleModal
                handleOpenClose={() => setItem(null)}
                openDefault={!!item}
                content={
                    <div className={modalClasses.bigModal}>
                        <ServiceAppt modalDate={item} handleOpenClose={() => setItem(null)} />
                    </div>
                }
            />
        </>
    );
};
